import { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Spinner } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PlatformCosts, SpendChart, SpendTable, usd } from "@/components/admin/SpendBreakdown";
import { Visitors } from "@/components/admin/Visitors";

const AX = { tick: { fontSize: 11, fill: "#64748b" } };

const Tile = ({ label, value, sub, testid }) => (
  <div className="rounded-xl bg-[#F5F1EB] p-4" data-testid={testid}><p className="text-xs text-slate-500">{label}</p><p className="text-3xl font-semibold">{value}</p>{sub && <p className="text-xs text-slate-500">{sub}</p>}</div>
);

const Daily = ({ rows, k, fmt, color }) => (
  <ResponsiveContainer width="100%" height={180}>
    <BarChart data={rows} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
      <CartesianGrid stroke="#e2e8f0" vertical={false} />
      <XAxis dataKey="day" tickFormatter={(d) => d.slice(5)} minTickGap={20} {...AX} /><YAxis allowDecimals={false} tickFormatter={fmt} {...AX} />
      <Tooltip formatter={(v) => [fmt ? fmt(v) : v]} />
      <Bar dataKey={k} fill={color} radius={[3, 3, 0, 0]} maxBarSize={28} isAnimationActive={false} />
    </BarChart>
  </ResponsiveContainer>
);

const Users = ({ u, countries }) => (
  <section className="surface space-y-5 p-5" data-testid="analytics-users">
    <h2 className="text-2xl">Members</h2>
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
      <Tile label="Real members" value={u.total} sub={`excludes ${u.sample} sample accounts`} testid="analytics-users-total" />
      <Tile label="New in 7 days" value={u.new_7} testid="analytics-users-new7" />
      <Tile label="New in 30 days" value={u.new_30} testid="analytics-users-new30" />
      <Tile label="Posted in 30 days" value={u.posted_30} testid="analytics-users-active" />
    </div>
    <div><h3 className="text-lg">Sign-ups, last 30 days</h3><Daily rows={u.signups} k="n" color="#0f766e" /></div>
    <div data-testid="analytics-countries"><h3 className="mb-2 text-lg">By country</h3>
      <div className="space-y-1.5">{countries.map((c) => (
        <div key={c.country} className="flex items-center gap-3 text-sm" data-testid={`analytics-country-${c.country}`}>
          <span className="w-40 shrink-0 truncate">{c.country}</span>
          <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-[#EFE9E1]"><div className="h-full rounded-full bg-slate-800" style={{ width: `${(c.users / Math.max(1, countries[0].users)) * 100}%` }} /></div>
          <span className="w-16 text-right tabular-nums text-slate-600">{c.users} · {Math.round((c.users / Math.max(1, u.total)) * 100)}%</span>
        </div>
      ))}</div>
      <p className="mt-2 text-[11px] text-slate-400">From the approximate area members set. Sample accounts are excluded.</p>
    </div>
  </section>
);

const Prices = ({ models, onSave }) => {
  const [p, setP] = useState(Object.fromEntries(models.map((m) => [m.model, m.price])));
  return (
    <div className="space-y-2" data-testid="analytics-prices">
      <h3 className="text-lg">By model this month</h3>
      <div className="overflow-x-auto"><table className="w-full text-left text-sm">
        <thead className="text-xs text-slate-500"><tr><th className="py-1 pr-3">Model</th><th className="pr-3">Calls</th><th className="pr-3">Est. cost per call (US$)</th><th>Est. cost</th></tr></thead>
        <tbody>{models.map((m) => (
          <tr key={m.model} className="border-t border-border"><td className="py-1.5 pr-3 font-mono text-xs">{m.model}</td><td className="pr-3">{m.calls}</td>
            <td className="pr-3"><Input type="number" step="0.001" min={0} className="h-8 w-28" value={p[m.model]} onChange={(e) => setP({ ...p, [m.model]: e.target.value })} data-testid={`price-${m.model}`} /></td>
            <td>{usd(m.cost)}</td></tr>
        ))}</tbody>
      </table></div>
      <Button size="sm" className="rounded-full" onClick={() => onSave(p)} data-testid="analytics-prices-save">Save cost estimates</Button>
    </div>
  );
};

const Spend = ({ ai, onSave, onPlatform }) => {
  const s = ai.spend;
  const month = new Date(`${s.next_month}-01T00:00:00`).toLocaleDateString(undefined, { month: "long", year: "numeric" });
  return (
    <section className="surface space-y-5 p-5" data-testid="analytics-ai">
      <div><h2 className="text-2xl">AI & platform spend (estimated)</h2><p className="mt-1 text-sm text-slate-500">AI calls and emails are counted by the app and multiplied by your estimated costs. Fixed Emergent costs come from the amounts you enter. Your actual Emergent bill may differ. All figures in US dollars.</p></div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Tile label="Today" value={usd(s.totals.today, 3)} testid="analytics-ai-today" />
        <Tile label="This month to date" value={usd(s.totals.this_month)} testid="analytics-ai-month" />
        <Tile label="Since tracking began" value={usd(s.totals.all_time)} sub={s.tracking_since ? `usage since ${s.tracking_since}` : ""} testid="analytics-ai-all-time" />
        <Tile label={`Forecast for ${month}`} value={usd(s.totals.forecast)} sub={`AI cap ceiling ${usd(ai.forecast.cap_ceiling)}`} testid="analytics-ai-forecast" />
      </div>
      <SpendTable s={s} />
      <p className="text-xs text-slate-500" data-testid="analytics-forecast-basis">Usage forecasts = average daily spend over the last {s.based_on_days} day{s.based_on_days === 1 ? "" : "s"} × days next month. Fixed costs are forecast at their monthly amount. Forecasts become more reliable after two weeks of data.</p>
      <div><h3 className="text-lg">Daily usage spend, last 30 days</h3><SpendChart rows={s.chart} /></div>
      <PlatformCosts key={JSON.stringify(s.platform)} platform={s.platform} onSave={onPlatform} />
      <Prices models={ai.by_model} onSave={onSave} />
    </section>
  );
};

export default function AdminAnalytics() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!d) return <Spinner />;
  const put = (path, body, msg) => api.put(path, body).then(({ data }) => { setD(data); toast.success(msg); }).catch((e) => toast.error(errMsg(e)));
  const save = (prices) => put("/admin/analytics/prices", { prices }, "Cost estimates saved");
  const savePlatform = (body) => put("/admin/analytics/platform", body, "Platform costs saved");
  return (
    <div className="space-y-8" data-testid="admin-analytics-page">
      <PageHeader env="admin" title="Analytics" subtitle="Visitors, members, where they are, and what AI and the platform are costing, at a glance." />
      <Visitors />
      <Users u={d.users} countries={d.countries} />
      <Spend ai={d.ai} onSave={save} onPlatform={savePlatform} />
    </div>
  );
}
